import { NavLink, Outlet } from 'react-router';
import type { Permission } from '@mys/contracts';
import { useSession } from '../lib/session.js';
import { useCan } from '../lib/permissions.js';

/* ============================================================================
   The frame every signed-in screen sits in.
   ----------------------------------------------------------------------------
   Below 768px the sidebar becomes a horizontally scrolling top bar. The owner
   checks this from a phone, one-handed, standing in a shop.
   ========================================================================== */

/**
 * Every link carries the permission its screen needs, and a link whose
 * permission this account lacks is not rendered.
 *
 * The permission, not the role: the nav and the API then refuse the same
 * things for the same reason. Overview, Security and Settings have none —
 * Overview shows only the tiles its data allows, and the other two are about
 * this account rather than about a module. Everything else maps to the
 * `<module>:view` the API gates that screen's reads on, so a `support`
 * account is never shown an Invoices tab that answers 403.
 */
const LINKS: { to: string; label: string; end: boolean; permission?: Permission }[] = [
  { to: '/', label: 'Overview', end: true },
  { to: '/leads', label: 'Leads', end: false, permission: 'leads:view' },
  { to: '/clients', label: 'Clients', end: false, permission: 'clients:view' },
  { to: '/projects', label: 'Projects', end: false, permission: 'projects:view' },
  { to: '/invoices', label: 'Invoices', end: false, permission: 'invoices:view' },
  { to: '/care-plans', label: 'Care plans', end: false, permission: 'care-plans:view' },
  { to: '/edit-requests', label: 'Changes', end: false, permission: 'edit-requests:view' },
  { to: '/blog', label: 'Blog', end: false, permission: 'posts:view' },
  { to: '/testimonials', label: 'Testimonials', end: false, permission: 'testimonials:view' },
  { to: '/analytics', label: 'Analytics', end: false, permission: 'analytics:view' },
  { to: '/security', label: 'Security', end: false },
  /* `end` on Settings, and it matters: /settings/roles sits underneath it, and
     without this both tabs would light up as the current page at once. */
  { to: '/settings', label: 'Settings', end: true },
  { to: '/settings/roles', label: 'Roles', end: false, permission: 'settings:team' },
];

export function Shell() {
  const { user, logout } = useSession();
  const may = useCan();
  const links = LINKS.filter((link) => !link.permission || may(link.permission));

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Seedha content par jayein
      </a>

      <nav className="sidebar" aria-label="Sections">
        <div className="sidebar__brand">makeyoursite</div>

        {links.map((link) => (
          <NavLink key={link.to} to={link.to} end={link.end} className="sidebar__link">
            {link.label}
          </NavLink>
        ))}

        {/* The styling moved out of `style={{}}` and into app.css so the
            responsive rules can reach it. It could not before, and the result
            was that this whole block was `display: none` below 768px — see the
            note beside that breakpoint. An inline style always wins, which is
            exactly the wrong property for anything that has to change size. */}
        <div className="sidebar__foot">
          <div className="sidebar__user">{user?.name ?? user?.email}</div>
          <button
            type="button"
            className="btn btn--ghost btn--small sidebar__logout"
            onClick={() => void logout()}
          >
            Logout
          </button>
        </div>
      </nav>

      <main className="main" id="main">
        <Outlet />
      </main>
    </div>
  );
}
